<script lang="ts">
  import Plus from '@lucide/svelte/icons/plus';
  import ArrowUpRight from '@lucide/svelte/icons/arrow-up-right';
  import type { FaqItem } from '$lib/utils/faq-content';
  import { CONTACT_PATH } from '$lib/utils/site';
  import { scrollToContactForm } from '$lib/utils/contact-scroll';

  let {
    items,
    title = 'A few things',
    titleSecondLine = 'you might ask.'
  }: { items: FaqItem[]; title?: string; titleSecondLine?: string } = $props();

  function openContact(event: MouseEvent) {
    if (!scrollToContactForm()) return;

    event.preventDefault();
  }
</script>

<section id="faq" class="faq-section">
  <div class="faq-intro">
    <h2>{title}<br />{titleSecondLine}</h2>
    <p>Need a hand with your institution?</p>
    <a href={CONTACT_PATH} onclick={openContact}>Talk to the Edvello team <ArrowUpRight size={14} /></a>
  </div>
  <div class="questions">
    {#each items as item}<details>
        <summary>{item.question}<span class="summary-icon"><Plus size={16} /></span></summary>
        <p>{item.answer}</p>
      </details>{/each}
  </div>
</section>

<style>
  .faq-section {
    width: min(1120px, calc(100% - 80px));
    margin-inline: auto;
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 90px;
    padding-block: 110px;
  }
  h2 {
    margin: 0;
    font-size: clamp(36px, 4.3vw, 54px);
    font-weight: 500;
    line-height: 1.13;
    letter-spacing: -0.04em;
    text-wrap: balance;
  }
  .faq-intro {
    align-self: start;
    position: sticky;
    top: 130px;
  }
  .faq-intro > p {
    margin: 24px 0 4px;
    color: #6a7a94;
    font-size: 13px;
    line-height: 1.9;
  }
  .faq-intro > a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #1447e6;
    font-size: 13px;
  }
  .faq-intro > a:hover {
    text-decoration: underline;
  }
  details {
    border-bottom: 1px solid #dce3f1;
  }
  details:first-child {
    border-top: 1px solid #dce3f1;
  }
  summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    padding-block: 23px;
    list-style: none;
    cursor: pointer;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;
    transition: color 200ms;
  }
  summary:hover {
    color: #1447e6;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  .summary-icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #edf2fc;
    color: #4f6a96;
    transition:
      rotate 350ms cubic-bezier(0.16, 1, 0.3, 1),
      background 200ms,
      color 200ms;
  }
  details[open] .summary-icon {
    rotate: 45deg;
    background: #1447e6;
    color: #fff;
  }
  details p {
    margin: 0;
    padding: 0 48px 24px 0;
    color: #62738d;
    font-size: 14px;
    line-height: 1.8;
  }
  @supports (interpolate-size: allow-keywords) {
    details {
      interpolate-size: allow-keywords;
    }
    details::details-content {
      height: 0;
      overflow: clip;
      transition:
        height 420ms cubic-bezier(0.16, 1, 0.3, 1),
        content-visibility 420ms allow-discrete;
    }
    details[open]::details-content {
      height: auto;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    details::details-content {
      transition: none;
    }
  }
  @media (max-width: 760px) {
    .faq-section {
      width: calc(100% - 40px);
      grid-template-columns: 1fr;
      gap: 29px;
      padding-block: 64px;
    }
    .faq-intro {
      position: static;
    }
    h2 {
      font-size: 36px;
    }
    summary {
      font-size: 14px;
      padding-block: 20px;
    }
    details p {
      padding-right: 0;
      font-size: 13px;
    }
  }
</style>
